<template>
  <v-card density="compact">
    <v-form lazy-validation :ref="formRef" :disabled="disabled">
      <v-card-title>
        <v-row no-gutters>
          <v-col>{{newItem ? 'New' : 'Edit'}} Data Source</v-col>
        </v-row>
      </v-card-title>
      <v-dialog v-model="dialogDelete" max-width="500px">
        <v-card>
          <v-card-title>Confirm delete</v-card-title>
          <v-card-text>Are you sure that you want to delete this item/section?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn variant="text" @click="closeDeleteDialog">Cancel</v-btn>
            <v-btn variant="text" @click="deleteItemConfirm(currentDeleteItem[0], currentDeleteItem[1])">OK</v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="dialogDeleteFile" max-width="500px">
        <v-card>
          <v-card-title>Confirm delete file</v-card-title>
          <v-card-text>Are you sure that you want to delete this file?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn variant="text" @click="closeDeleteFileDialog">Cancel</v-btn>
            <v-btn variant="text" @click="deleteFileConfirm(currentDeleteFile[0], currentDeleteFile[1])">OK</v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-divider></v-divider>
      <v-sheet elevation="0" max-height="2000" class="overflow-y-auto">
        <v-card-text>
          <v-container>
            <v-row no-gutters>
              <v-col cols="12">
                <v-text-field v-model="targetDataSource.name" :rules="rules.name" label="Data source name"></v-text-field>
              </v-col>
            </v-row>
            <br/>
            <div v-if="groupItems.length > 1">
              <v-row no-gutters>
                <v-col cols="4">
                  <v-label class="text-wrap" style="font-size: 1em">Owner company</v-label>
                </v-col>
                <v-col cols="8">
                  <v-select v-model="targetDataSource.owner"
                            :items="groupItems"
                            :rules="rules.owner"
                            :item-title="(item) => item.name"
                            :item-value="(item) => item.id"
                            placeholder="Owner company"
                            density="compact"
                            :disabled="!newItem">
                  </v-select>
                </v-col>
              </v-row>
              <br/>
            </div>
            <h3>Attached documents</h3>
            <br/>
            <v-row>
              <v-col>
                <v-chip v-for="(file, i) in targetDataSource.files" :key="i" class="me-2"
                        color="primary" size="small" :label="true"
                        @click.stop="openFile(targetDataSource.id, file.filename, file.contentType)">
                  <template v-slot:append>
                    <v-icon class="ms-2" size="small" v-tooltip="'Delete file'"
                            @click.stop="currentDeleteFile = [targetDataSource.files, i]; dialogDeleteFile = true">
                      mdi-delete
                    </v-icon>
                  </template>
                  <template v-slot:default>
                    {{ file.filename }}
                  </template>
                </v-chip>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-file-input v-model="targetDataSource.newFiles"
                              label="Additional documents"
                              prepend-icon="mdi-paperclip"
                              accept=".bmp,image/bmp,
                            .csv,text/csv,
                            .doc,application/msword,
                            .docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                            .gif,image/gif,
                            .jpeg,.jpg,image/jpeg,
                            .png,image/png,
                            .pdf,application/pdf,
                            .ppt,application/vnd.ms-powerpoint,
                            .pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation,
                            .svg,image/svg+xml,
                            .txt,text/plain,
                            .tif,.tiff,image/tiff,
                            .xls,application/vnd.ms-excel,
                            .xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                              variant="outlined"
                              :multiple="true">
                  <template v-slot:selection="{ fileNames }">
                    <template v-for="fileName in fileNames" :key="fileName">
                      <v-chip class="me-2" color="primary" size="small" label>
                        {{ fileName }}
                      </v-chip>
                    </template>
                  </template>
                </v-file-input>
              </v-col>
            </v-row>
            <br/>
            <br/>
            <h3>General information about the data source</h3>
            <v-divider :vertical="true"></v-divider>
            <v-row no-gutters>
              <v-col cols="4">
                <v-label class="text-wrap" style="font-size: 1em">Therapeutic area</v-label>
              </v-col>
              <v-col cols="8">
                <v-combobox v-model="targetDataSource.therapeuticArea"
                            :items="therapeuticAreaItems"
                            :rules="rules.therapeuticArea"
                            placeholder="Therapeutic area"
                            density="compact"
                            multiple></v-combobox>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="4">
                <v-label class="text-wrap" style="font-size: 1em">Countries</v-label>
              </v-col>
              <v-col cols="8">
                <v-combobox v-model="targetDataSource.country"
                            :items="countryItems"
                            :rules="rules.country"
                            placeholder="Countries"
                            density="compact"
                            multiple></v-combobox>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="4">
                <v-label class="text-wrap" style="font-size: 1em">Comment to countries</v-label>
              </v-col>
              <v-col cols="8">
                <v-text-field v-model="targetDataSource.countryComment"
                              placeholder="Comment to countries"
                              density="compact"></v-text-field>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="4">
                <v-label class="text-wrap" style="font-size: 1em">Regional coverage</v-label>
              </v-col>
              <v-col cols="8">
                <v-textarea v-model="targetDataSource.regionalCoverage"
                            placeholder="Regional coverage" rows="2" variant="filled" density="compact"
                            auto-grow shaped></v-textarea>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="4">
                <v-label class="text-wrap" style="font-size: 1em">Geographical set-up</v-label>
              </v-col>
              <v-col cols="8">
                <v-combobox v-model="targetDataSource.geographicalSetUp"
                            :items="geographicalSetUpItems"
                            :rules="rules.geographicalSetUp"
                            placeholder="Geographical set-up"
                            density="compact"
                            multiple></v-combobox>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="4">
                <v-label class="text-wrap" style="font-size: 1em">Data source type</v-label>
              </v-col>
              <v-col cols="8">
                <v-combobox v-model="targetDataSource.dataSourceType"
                            :items="dataSourceTypeItems"
                            :rules="rules.dataSourceType"
                            placeholder="Data source type"
                            density="compact"
                            multiple></v-combobox>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="4">
                <v-label class="text-wrap" style="font-size: 1em">Retrospective or Prospective collection</v-label>
              </v-col>
              <v-col cols="8">
                <v-combobox v-model="targetDataSource.retrospectiveOrProspectiveCollection"
                            :items="retrospectiveOrProspectiveCollectionItems"
                            placeholder="Retrospective or Prospective collection"
                            density="compact"></v-combobox>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="4">
                <v-label class="text-wrap" style="font-size: 1em">Longitudinal data collection</v-label>
              </v-col>
              <v-col cols="8">
                <v-combobox v-model="targetDataSource.longitudinalDataCollection"
                            :items="longitudinalDataCollectionItems"
                            placeholder="Longitudinal data collection"
                            density="compact"></v-combobox>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="4">
                <v-label class="text-wrap" style="font-size: 1em">Outreach performed</v-label>
              </v-col>
              <v-col cols="8">
                <v-combobox v-model="targetDataSource.outreachPerformed"
                            :items="outreachPerformedItems"
                            placeholder="Outreach performed"
                            density="compact"></v-combobox>
              </v-col>
            </v-row>
            <br/>

            <h3>Short description/Abstract</h3>
            <v-divider :vertical="true"></v-divider>
            <v-row no-gutters>
              <v-col cols="12">
                <v-textarea v-model="targetDataSource.shortDescriptionAbstract" :rules="rules.shortDescriptionAbstract"
                            placeholder="Short description/Abstract" rows="2" variant="filled" density="compact"
                            auto-grow shaped></v-textarea>
              </v-col>
            </v-row>
            <br/>

            <h3>Publication information</h3>
            <v-divider :vertical="true"></v-divider>
            <v-row no-gutters>
              <v-col cols="4">
                <v-label class="text-wrap" style="font-size: 1em">Publications</v-label>
              </v-col>
              <v-col cols="8">
                <v-textarea v-model="targetDataSource.publication"
                            placeholder="Publications" rows="2" variant="filled" density="compact"
                            auto-grow shaped></v-textarea>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="4">
                <v-label class="text-wrap" style="font-size: 1em">Link to data source website</v-label>
              </v-col>
              <v-col cols="8">
                <v-text-field v-model="targetDataSource.linkToDataSourceWebsite"
                              placeholder="Link to data source website"
                              density="compact"></v-text-field>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="4">
                <v-label class="text-wrap" style="font-size: 1em">Other relevant websites</v-label>
              </v-col>
              <v-col cols="8">
                <v-text-field v-model="targetDataSource.otherRelevantWebsites"
                              placeholder="Other relevant websites"
                              density="compact"></v-text-field>
              </v-col>
            </v-row>
            <br/>

            <v-row no-gutters>
              <v-col cols="12" class="text-right">
                <v-btn variant="plain" @click="itemsInDynamicSectionsShown = !itemsInDynamicSectionsShown">
                  {{ itemsInDynamicSectionsShown ? 'Hide items' : 'Show items'}}
                </v-btn>
              </v-col>
            </v-row>
            <br/>

            <draggable v-model="targetDataSource.dataSourceCharacteristics"
                       ghost-class="ghost"
                       handle=".handle"
                       :item-key="item => targetDataSource.dataSourceCharacteristics.indexOf(item)">
              <template #item="{element: data, index: dataIndex}">
                <div>
                  <v-row no-gutters>
                    <v-col cols="10">
                      <v-text-field class="font-weight-bold"
                                    v-model="targetDataSource.dataSourceCharacteristics[dataIndex].title"></v-text-field>
                    </v-col>
                    <v-col cols="1" class="text-center handle">
                      <v-icon size="small" class="me-2"
                              v-tooltip="'Drag section'">
                        mdi-drag
                      </v-icon>
                    </v-col>
                    <v-col cols="1" class="text-right">
                      <v-icon size="small" class="me-2"
                              v-tooltip="'Move section up'"
                              @click.stop="moveItemUp(targetDataSource.dataSourceCharacteristics, dataIndex)"
                              :disabled="disabled || (dataIndex === 0)">
                        mdi-arrow-up
                      </v-icon>
                      <v-icon size="small" class="me-2"
                              v-tooltip="'Move section down'"
                              @click.stop="moveItemDown(targetDataSource.dataSourceCharacteristics, dataIndex)"
                              :disabled="disabled || (dataIndex === targetDataSource.dataSourceCharacteristics.length - 1)">
                        mdi-arrow-down
                      </v-icon>
                      <v-icon size="small" class="me-2"
                              v-tooltip="'Delete section'"
                              :disabled="disabled"
                              @click.stop="currentDeleteItem = [targetDataSource.dataSourceCharacteristics, dataIndex]; dialogDelete = true">
                        mdi-delete
                      </v-icon>
                    </v-col>
                  </v-row>
                  <br/>
                  <div v-if="itemsInDynamicSectionsShown">
                    <draggable v-model="data.items"
                               ghost-class="ghost"
                               handle=".handle"
                               :item-key="item => data.items.indexOf(item)">
                      <template #item="{element: labelValue, index}">
                        <div>
                          <v-row no-gutters>
                            <v-col cols="4">
                              <v-textarea v-model="labelValue.label"
                                          rows="1" variant="filled" density="compact"
                                          auto-grow shaped></v-textarea>
                            </v-col>
                            <v-col cols="1" class="text-center handle">
                              <v-icon size="small" class="me-2"
                                      v-tooltip="'Drag item'">
                                mdi-drag
                              </v-icon>
                            </v-col>
                            <v-col cols="6">
                              <v-textarea v-model="labelValue.value"
                                          rows="1" variant="filled" density="compact"
                                          :placeholder="labelValue.label"
                                          auto-grow shaped></v-textarea>
                            </v-col>
                            <v-col cols="1" class="text-right">
                              <v-icon size="small" class="me-2"
                                      v-tooltip="'Move item up'"
                                      @click.stop="moveItemUp(targetDataSource.dataSourceCharacteristics[dataIndex].items, index)"
                                      :disabled="disabled || (index === 0)">
                                mdi-arrow-up
                              </v-icon>
                              <v-icon size="small" class="me-2"
                                      v-tooltip="'Move item down'"
                                      @click.stop="moveItemDown(targetDataSource.dataSourceCharacteristics[dataIndex].items, index)"
                                      :disabled="disabled || (index === data.items.length - 1)">
                                mdi-arrow-down
                              </v-icon>
                              <v-icon size="small" class="me-2"
                                      v-tooltip="'Delete item'"
                                      :disabled="disabled"
                                      @click.stop="currentDeleteItem = [targetDataSource.dataSourceCharacteristics[dataIndex].items, index]; dialogDelete = true">
                                mdi-delete
                              </v-icon>
                            </v-col>
                          </v-row>
                        </div>
                      </template>
                    </draggable>
                    <v-row no-gutters>
                      <v-col cols="12" class="text-right">
                        <v-btn @click="addItem(targetDataSource.dataSourceCharacteristics, dataIndex)" :disabled="disabled">
                          Add new row
                        </v-btn>
                      </v-col>
                    </v-row>
                  </div>
                  <br/>
                </div>
              </template>
            </draggable>
            <v-row no-gutters>
              <v-col cols="12" class="text-right">
                <v-btn @click="addSection(targetDataSource.dataSourceCharacteristics)" :disabled="disabled">
                  Add new section
                </v-btn>
              </v-col>
            </v-row>

            <div v-if="dataSource.externalId && dataSource.importSource">
              <h3>Originally imported from data source</h3>
              <p>{{dataSource.importSource}} ({{dataSource.externalId}})</p>
              <br/>
            </div>
            <div v-if="dataSource.customer">
              <h3>Originally delivered to customer</h3>
              <p>{{customers.get(dataSource.customer)?.name || 'You do not have access to this customer.'}}</p>
              <br/>
            </div>
          </v-container>
        </v-card-text>
      </v-sheet>
    </v-form>
  </v-card>
</template>

<script>
import {defineComponent} from 'vue';
import axios from "axios";
import draggable from 'vuedraggable'

export default defineComponent({
  name: 'LandscapingDataSourceEditCard',
  props: {
    formRef: String,
    dataSource: Object,
    groupItems: Array,
    customers: Map,
    therapeuticAreaItems: Array,
    countryItems: Array,
    geographicalSetUpItems: Array,
    dataSourceTypeItems: Array,
    retrospectiveOrProspectiveCollectionItems: Array,
    longitudinalDataCollectionItems: Array,
    outreachPerformedItems: Array,
    disabled: Boolean,
    newItem: {
      type: Boolean,
      default: false
    }
  },
  components: {
    draggable
  },
  data: () => ({
    targetDataSource: {},
    itemsInDynamicSectionsShown: true,
    dialogDisabled: false,
    dialogDelete: false,
    dialogDeleteFile: false,
    currentDeleteItem: null,
    currentDeleteFile: null,
    rules: {
      name: [
        value => {
          if (value) return true
          return 'You must enter a data source name.'
        }
      ],
      owner: [
        value => {
          if (value) return true
          return 'You must enter an owner company.'
        }
      ],
      therapeuticArea: [
        value => {
          if (value?.length > 0) return true
          return 'You must select at least one therapeutic area.'
        }
      ],
      country: [
        value => {
          if (value?.length > 0) return true
          return 'You must select at least one country.'
        }
      ],
      geographicalSetUp: [
        value => {
          if (value?.length > 0) return true
          return 'You must select at least one geographical set-up.'
        }
      ],
      dataSourceType: [
        value => {
          if (value?.length > 0) return true
          return 'You must select at least one data source type.'
        }
      ],
      shortDescriptionAbstract: [
        value => {
          if (value) return true
          return 'You must enter a short description/abstract.'
        }
      ]
    }
  }),
  watch: {
    targetDataSource: {
      deep: true,
      handler: function(newTargetDataSource) {
        let targetData = {...newTargetDataSource}
        let newFilesAdded = targetData.newFiles?.length > 0
        delete targetData.newFiles
        let modifiedTargetDataSource = newFilesAdded || this.isModified(this.dataSource, targetData)
        this.$emit('modified', modifiedTargetDataSource)
      }
    }
  },
  methods: {
    openFile(id, file, contentType) {
      axios.get('/data-landscaping/data-source-file',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            responseType: 'blob',
            params: {
              data_source_id: id,
              file_name: file,
              content_type: contentType
            }
          })
          .then((response) => {
            const href = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('target', '_blank');
            link.click();
            URL.revokeObjectURL(href);
          });
    },
    validateAndSave() {
      this.$refs[this.formRef].validate().then(validationResult => {
        if (validationResult.valid) {
          let deletedFiles = this.dataSource.files?.filter(file =>
              !this.targetDataSource.files?.some(targetFile => targetFile.filename === file.filename
                  && targetFile.contentType === file.contentType))

          let targetData = Object.assign({}, this.targetDataSource, {})
          delete targetData.files
          delete targetData.newFiles

          this.$emit('save', [targetData, deletedFiles || [], this.targetDataSource.newFiles || []])
        }
      })
    },
    moveItemUp(items, index) {
      if (index > 0) {
        const item = items[index]
        items.splice(index, 1)
        items.splice(index - 1, 0, item)
      }
    },
    moveItemDown(items, index) {
      if (index < items.length - 1) {
        const item = items[index]
        items.splice(index, 1)
        items.splice(index + 1, 0, item)
      }
    },
    addItem(items, index) {
      items[index].items.push({label: '', value: ''})
    },
    addSection(items) {
      items.push({title: '', items: []})
    },
    deleteItemConfirm(items, index) {
      items.splice(index, 1)
      this.currentDeleteItem = null
      this.dialogDelete = false
    },
    closeDeleteDialog() {
      this.currentDeleteItem = null
      this.dialogDelete = false
    },
    deleteFileConfirm(files, index) {
      files.splice(index, 1)
      this.currentDeleteFile = null
      this.dialogDeleteFile = false
    },
    closeDeleteFileDialog() {
      this.currentDeleteFile = null
      this.dialogDeleteFile = false
    },
    isModified(original, target) {
      return JSON.stringify(original) !== JSON.stringify(target)
    }
  },
  created() {
    this.targetDataSource = structuredClone(this.dataSource)
    if (this.newItem && !this.targetDataSource.owner && (this.groupItems.length === 1)) {
      this.targetDataSource.owner = this.groupItems[0].id
    }
    if (!this.targetDataSource.dataSourceCharacteristics) {
      this.targetDataSource.dataSourceCharacteristics = []
    }
  }
});
</script>

<style lang="scss" scoped>
.handle {
  cursor: grab;
}

.ghost {
  opacity: 0.2;
  background: #ffffff;
}
</style>
